import React, { useState, useEffect, useRef } from 'react';
import {
  Play,
  Pause,
  RotateCcw,
  Volume2,
  VolumeX,
  Maximize2,
  Calendar,
  Briefcase,
  Layers,
  Users,
  Clock,
  ShieldCheck,
  CheckCircle2
} from 'lucide-react';

const CHAPTERS = [
  {
    time: 0,
    label: '0:00 Leave Management',
    title: 'Leave Requests & Team Balances',
    desc: 'Paid leave balance tracking, request approvals, and dynamic company holiday calendar.',
    icon: Calendar
  },
  {
    time: 5,
    label: '0:05 Recruitment Admin',
    title: 'Recruitment Dashboard & Roles',
    desc: 'Real-time monitoring of open requisitions, active recruiter allocations, and application volumes.',
    icon: Briefcase
  },
  {
    time: 10,
    label: '0:10 Job Submissions',
    title: 'Client Submissions & Progress',
    desc: 'Multi-stage candidate submission pipeline with Onshore/Offshore distribution filters.',
    icon: Layers
  },
  {
    time: 15,
    label: '0:15 Talent Sourcing',
    title: 'Active Sourcing & Resume Parsing',
    desc: 'Intelligent search across talent database with AI keyword matching and automated profile extraction.',
    icon: Users
  },
  {
    time: 20,
    label: '0:20 ATS Tracking',
    title: 'Applicant Tracking & Scoring',
    desc: 'Complete candidate lifecycle with automated screening scores, position mappings, and stage transitions.',
    icon: ShieldCheck
  },
  {
    time: 25,
    label: '0:25 Timesheet Approvals',
    title: '1-Click Approvals & Audit Trail',
    desc: 'Multi-company weekly timesheet submissions with 1-click supervisor sign-off and instant billing sync.',
    icon: Clock
  }
];

const DemoVideoSection = ({ videoSrc = `${process.env.PUBLIC_URL || ''}/videos/demo_video_prophecy.mp4` }) => {
  const [isPlaying, setIsPlaying] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(29);
  const [isMuted, setIsMuted] = useState(true); // Muted by default so browsers allow seamless autoplay
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  // Active chapter based on currentTime
  const currentChapter = CHAPTERS.slice().reverse().find((c) => currentTime >= c.time) || CHAPTERS[0];

  // Auto-run video on mount
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch((err) => {
        console.log('Autoplay waiting for interaction:', err);
      });
    }
  }, []);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play().catch((err) => console.log('Playback error:', err));
    }
  };

  const handleSeek = (time) => {
    setCurrentTime(time);
    if (videoRef.current) {
      videoRef.current.currentTime = time;
    }
  };

  const handleChapterClick = (time) => {
    setCurrentTime(time);
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      videoRef.current.play().catch((err) => console.log('Playback error:', err));
      setIsPlaying(true);
    }
  };

  const handleReset = () => {
    setCurrentTime(0);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch((err) => console.log('Playback error:', err));
      setIsPlaying(true);
    }
  };

  const toggleFullScreen = () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen?.().catch((err) => console.log(err));
    } else {
      document.exitFullscreen?.().catch((err) => console.log(err));
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  return (
    <section className="erp-video-section-container" id="video-demo">
      <div className="jp-workspace-container">
        {/* Section Header */}
        <div className="erp-clean-header" style={{ marginBottom: '24px' }}>
          <div className="erp-clean-badge">
            <Play size={12} />
            <span>30-Second Product Walkthrough</span>
          </div>
          <h2 className="erp-clean-title">
            Watch Prophecy Nexus in Action (Product Tour)
          </h2>
          <p className="erp-clean-desc">
            Explore authentic workflows: from leave management and candidate sourcing to applicant tracking and 1-click timesheet approvals.
          </p>
        </div>

        {/* Browser Mac-Style Player Frame */}
        <div className="erp-video-player-frame" ref={containerRef}>
          {/* macOS Browser Header */}
          <div className="erp-video-frame-header">
            <div className="erp-mac-dots">
              <span className="erp-mac-dot dot-red" />
              <span className="erp-mac-dot dot-yellow" />
              <span className="erp-mac-dot dot-green" />
            </div>

            <div className="erp-browser-address-bar">
              <span>https://hub.prophecytechs.com/workspace</span>
            </div>

            <div style={{ width: '80px', textAlign: 'right' }}>
              <span style={{ fontSize: '11px', color: '#2563eb', fontWeight: '800', background: '#eff6ff', padding: '2px 8px', borderRadius: '10px' }}>
                AUTO LOOP
              </span>
            </div>
          </div>

          {/* Video Player Display Area with Centered Play Button Overlay when paused */}
          <div className="erp-video-screen-viewport" onClick={togglePlay} style={{ cursor: 'pointer' }}>
            <video
              ref={videoRef}
              src={videoSrc}
              className="erp-actual-video-element"
              autoPlay
              loop
              muted={isMuted}
              playsInline
              preload="auto"
              onTimeUpdate={() => setCurrentTime(Math.floor(videoRef.current?.currentTime || 0))}
              onLoadedMetadata={() => setTotalDuration(Math.floor(videoRef.current?.duration || 29))}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
            >
              <source src={videoSrc} type="video/mp4" />
              Your browser does not support HTML5 video.
            </video>

            {/* Glowing Big Play Button Overlay When Paused */}
            {!isPlaying && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'rgba(15, 23, 42, 0.45)',
                  backdropFilter: 'blur(3px)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  transition: 'all 0.2s ease',
                  zIndex: 10
                }}
              >
                <div
                  style={{
                    width: '68px',
                    height: '68px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #2563eb, #38bdf8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#ffffff',
                    boxShadow: '0 8px 30px rgba(37, 99, 235, 0.6), 0 0 20px rgba(56, 189, 248, 0.5)',
                    transform: 'scale(1)',
                    transition: 'transform 0.2s ease'
                  }}
                >
                  <Play size={30} style={{ marginLeft: '4px' }} />
                </div>
                <div style={{ color: '#ffffff', fontWeight: '800', fontSize: '14px', textShadow: '0 2px 8px rgba(0,0,0,0.6)' }}>
                  Click to Watch 30-Sec Tour
                </div>
              </div>
            )}
          </div>

          {/* Interactive Player Controls Bar */}
          <div className="erp-video-controls-bar" onClick={(e) => e.stopPropagation()}>
            {/* Play/Pause & Replay Buttons */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button
                type="button"
                className="erp-video-play-btn"
                onClick={togglePlay}
                aria-label={isPlaying ? 'Pause Demo' : 'Play Demo'}
              >
                {isPlaying ? <Pause size={16} /> : <Play size={16} style={{ marginLeft: '2px' }} />}
              </button>

              <button
                type="button"
                className="erp-video-icon-btn"
                onClick={handleReset}
                title="Restart walkthrough video"
              >
                <RotateCcw size={15} />
              </button>

              <span className="erp-video-time-display">
                {formatTime(currentTime)} / {formatTime(totalDuration)}
              </span>
            </div>

            {/* Interactive Timeline Scrubber */}
            <div className="erp-video-timeline-wrap">
              <input
                type="range"
                min="0"
                max={totalDuration || 29}
                value={currentTime}
                onChange={(e) => handleSeek(Number(e.target.value))}
                className="erp-video-timeline-slider"
              />
            </div>

            {/* Volume & Fullscreen controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                type="button"
                className="erp-video-icon-btn"
                onClick={() => setIsMuted(!isMuted)}
                title={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted ? <VolumeX size={15} /> : <Volume2 size={15} />}
              </button>

              <button
                type="button"
                className="erp-video-icon-btn"
                onClick={toggleFullScreen}
                title="Fullscreen"
              >
                <Maximize2 size={15} />
              </button>
            </div>
          </div>

          {/* Chapter Scrubber Pills (Click to jump to that module moment) */}
          <div className="erp-video-chapters-row" onClick={(e) => e.stopPropagation()}>
            {CHAPTERS.map((chap) => {
              const isActive = currentChapter.time === chap.time;
              return (
                <button
                  key={chap.time}
                  type="button"
                  className={`erp-chapter-pill-btn ${isActive ? 'active' : ''}`}
                  onClick={() => handleChapterClick(chap.time)}
                  title={`${chap.title}: ${chap.desc}`}
                >
                  <span className="erp-chapter-dot" />
                  <span>{chap.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DemoVideoSection;
